import axios, { AxiosPromise } from 'axios';
import { find } from 'lodash';
import { getTranslate } from 'react-localize-redux';
import * as Redux from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { flagService } from '../../core/services';
import { EnqueueSnackbar } from '../../core/state/general/general.types';
import { activeLocaleInRedux } from '../../localization/index';
import { WebOrderTheme } from '../../style/ThemeProvider';
import { urlUtils } from '../../utils/urlUtils';
import * as analytics from '../analytics';
import * as Api from '../Api';
import * as Basket from '../BasketControl';
import { SubmitTypeOptions } from '../component/Checkout/types';
import * as Data from '../Data';
import * as Intercom from '../intercom';
import * as OrderFlow from '../OrderFlow';
import { userActions } from '../redux';
import { Routes } from '../Routes';
import { basketSelectors } from '../selectors';
import { getDeliveryLocations } from '../services/locationAPIServices';
import { storage } from '../services/storageServices';
import * as Store from '../Store';
import { Utilities } from '../Utilities';
import * as Vouchers from '../Vouchers';
import { clearBasket, removeFromBasket } from './basketActions';
import {
  FindInitialRestaurant,
  getLocationAreaData,
  getRestaurantData,
  LoadAppconfig,
  LoadCollectionDetails,
  LoadDeliveryDetails,
  LoadGeoIpLookup,
  LoadMenuByUrl as getMenuByUrl,
  LoadRestaurant,
  setupLanguage,
} from './loadingActions';
import {
  LoadDeliveryLocations,
  loadDeliveryLocations,
} from './user/userActions';

export {
  FindInitialRestaurant,
  getLocationAreaData,
  getMenuByUrl,
  getRestaurantData,
  LoadAppconfig,
  LoadCollectionDetails,
  LoadDeliveryDetails,
  LoadDeliveryLocations,
  loadDeliveryLocations,
  LoadGeoIpLookup,
  LoadRestaurant,
};

type ThunkResult<R> = ThunkAction<R, Store.State, undefined, Store.Action>;

const AddToBasket = (payload: Basket.Item): Store.AddToBasket => ({
  type: 'ADD_TO_BASKET',
  payload,
});
const ClearBasket = clearBasket;
const RemoveFromBasket = removeFromBasket;
const SetTip = (payload: number): Store.SetTip => ({
  type: 'BASKET_SET_TIP',
  payload,
});

export { AddToBasket, ClearBasket, RemoveFromBasket, SetTip };

export const ClearFlipdishStore = (): Store.ClearFlipdishStore => ({
  type: 'CLEAR_FLIPDISH_STORE',
});

export const PushAfterLogin = (url: string): Store.PushLoginUrl => ({
  type: 'PUSH_LOGIN_URL',
  payload: url,
});

export const SetSmsEnabled = (state: boolean): Store.SetSmsEnabled => ({
  type: 'SET_SMS_ENABLED',
  payload: state,
});

export const SetRequestedTime = (payload: {
  menuId: number;
  time: string;
}): Store.SetRequestedTime => ({
  type: 'SET_REQUESTED_TIME',
  payload,
});

export const setShowAddCardDialog = (
  payload: boolean,
): Store.SetShowAddCardDialog => ({
  type: 'SET_SHOW_ADD_CARD_DIALOG',
  payload,
});

export const SetCardOrder = (
  cardOrder: Store.CardOrder | null,
): Store.SetCardOrder => ({
  type: 'SET_CARD_ORDER',
  payload: cardOrder,
});

export const SetClientAnalytics = (
  clientAnalytics: boolean,
): Store.SetClientAnalytics => ({
  type: 'SET_CLIENT_ANALYTICS',
  payload: clientAnalytics,
});

export const SetCanCancel =
  (seconds: number) =>
  (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    dispatch({
      type: 'SET_CAN_CANCEL',
      payload: true,
    });

    setTimeout(() => {
      dispatch({
        type: 'SET_CAN_CANCEL',
        payload: false,
      });

      // If order hasn't been cancelled till then send ecommerce details
      const order = getState().flipdish.Order;

      if (order.state === 'paid') {
        analytics.eCommerce.purchase({
          // affiliation: `Flipdish - order - ${PhysicalRestaurantName}`,
          transaction_id: `${order.orderId}`,
          value: order.total,
          currency: Api.Currency[order.currency],
          items: order.basket.items.map((item) => ({
            id: `${item.Item.MenuItemId}`,
            name: item.Item.Name,
            price: item.Item.ActualPrice,
            quantity: 1, // TODO should group items
          })),
        });
      }
    }, seconds * 1000);
  };

export const SetMenuScroll = (top: number): Store.SetMenuScroll => ({
  type: 'SET_MENU_SCROLL',
  payload: top,
});

export const AddToast =
  (message: string) =>
  (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const toastMessages = getState().flipdish.ToastMessages;

    const messageNotInToastMessages =
      toastMessages.findIndex((toastMessage) => toastMessage === message) ===
      -1;

    if (messageNotInToastMessages) {
      dispatch({
        type: 'ADD_TOAST',
        payload: message,
      });

      setTimeout(() => {
        dispatch({ type: 'REMOVE_TOAST', payload: message });
      }, 5000);
    }
  };

export const SaveAddressSuggestion =
  (suggestion: string, postalCode: any) =>
  (dispatch: Redux.Dispatch<Redux.Action>) => {
    dispatch({
      type: 'ADD_ADDRESS_SUGGESTION',
      payload: {
        suggestion,
        postalCode,
      },
    });
  };

const AddDeliveryOption = (
  dl: Api.DeliveryLocation,
  option: Store.DeliveryOption,
): Store.AddDeliveryOption => ({
  type: 'ADD_DELIVERY_OPTION',
  payload: {
    dlid: dl.DeliveryLocationId,
    option,
  },
});

export const SetRestaurantUrl = (url: string): Store.SetRestaurantUrl => ({
  type: 'SET_RESTAURANT_URL',
  payload: url,
});

export const setPreselectedStoreVrid = (
  vrid: number,
): Store.setPreselectedStoreVrid => ({
  type: 'SET_PRESELECTED_STORE_VRID',
  payload: vrid,
});

export const SetCountryCode = (code: string) => ({
  type: 'SET_COUNTRY_CODE',
  payload: code,
});

export const AddLocation = (
  location: Api.Geo,
  certainty: Api.LocationCertainty,
): Store.AddLocation => ({
  type: 'ADD_LOCATION',
  payload: {
    certainty,
    geo: location,
  },
});

export const InitializeExpandedState = (
  vrid: number,
  expandedState: number[],
): Store.InitializeExpandedState => ({
  type: 'INITIALIZE_EXPANDED_STATE',
  payload: {
    vrid,
    expandedState,
  },
});

export const AddToExpandedState = (
  vrid: number,
  menuSectionId: number,
): Store.AddToExpandedState => ({
  type: 'ADD_TO_EXPANDED_STATE',
  payload: {
    menuSectionId,
    vrid,
  },
});

export const RemoveFromExpandedState = (
  vrid: number,
  menuSectionId: number,
): Store.RemoveFromExpandedState => ({
  type: 'REMOVE_FROM_EXPANDED_STATE',
  payload: {
    menuSectionId,
    vrid,
  },
});

export const EditChefNotes = (
  vrid: number,
  value: string,
): Store.EditChefNotes => ({
  type: 'EDIT_CHEF_NOTES',
  payload: {
    value,
    vrid,
  },
});

export const EditPickupLocationTypes = (
  vrid: number,
  value: Api.PickupLocationType,
): Store.EditPickupLocationTypes => ({
  type: 'EDIT_PICKUP_LOCATION_TYPES',
  payload: {
    value,
    vrid,
  },
});

export const EditTableNumbers = (
  vrid: number,
  value: number,
): Store.EditTableNumbers => ({
  type: 'EDIT_TABLE_NUMBERS',
  payload: {
    value,
    vrid,
  },
});

export const SetTableLock = (
  prid: number,
  isTableLockEnabled: boolean,
): Store.SetRestaurantCollectionTableLock => ({
  type: 'SET_RESTAURANT_COLLECTION_TABLE_LOCK',
  payload: {
    prid,
    isTableLockEnabled,
  },
});

export const SetOrder = (order: OrderFlow.State): Store.SetOrder => ({
  type: 'SET_ORDER',
  payload: order,
});

export const SetFallbackBaseUrl = (url: string): Store.SetFallbackBaseUrl => ({
  type: 'SET_FALLBACK_BASE_URL',
  payload: url,
});

export const SetOffset = (Offset: number): Store.SetOffset => ({
  type: 'SET_OFFSET',
  payload: Offset,
});

export const SetLiveOptions = (
  options: Api.MenuItemOptionSetItem[],
): Store.SetLiveOptions => ({
  type: 'SET_LIVE_OPTIONS',
  payload: options,
});

export const SetNextOptions = (
  prevScreenOptions: Api.MenuItemOptionSetItem[][],
  current: number,
  prevIndices: number[],
): Store.SetNextOptions => ({
  type: 'SET_NEXT_OPTIONS',
  payload: {
    PrevScreenOptions: prevScreenOptions,
    Current: current,
    PrevIndices: prevIndices,
  },
});

export const SetOptionsState = (
  options: Api.MenuItemOptionSetItem[],
  prevScreenOptions: Api.MenuItemOptionSetItem[][],
  current: number,
  prevIndices: number[],
): Store.SetOptionsState => ({
  type: 'SET_OPTIONS_STATE',
  payload: {
    Options: options,
    PrevScreenOptions: prevScreenOptions,
    Current: current,
    PrevIndices: prevIndices,
  },
});

const OrderSubmissionFailure = (
  dispatch: Redux.Dispatch<Redux.Action>,
  getState: () => Store.State,
  errorMsg: string,
  error: any,
  vrid: number,
  basket: Basket.Data,
  submitType: OrderFlow.SubmitOrderType,
  paymentAccount?: Api.PaymentAccount,
  _orderId?: number,
) => {
  const appconfig: any = {};
  const customer: any = {};
  const paymentaccount: any = {};

  const flipdishAppConfig = getState().flipdish.AppConfig;
  const flipdishCustomer = getState().flipdish.Customer;

  if (flipdishAppConfig.status === 'success') {
    appconfig.AppNameId = flipdishAppConfig.data.AppNameId;
    appconfig.WhiteLabelId = flipdishAppConfig.data.WhiteLabelId;
  }

  if (flipdishCustomer.status === 'success') {
    customer.CustomerName = flipdishCustomer.data.CustomerName;
    customer.EmailAddress = flipdishCustomer.data.EmailAddress;
  }

  if (paymentAccount) {
    paymentaccount.PaymentAccountId = paymentAccount.PaymentAccountId;
    paymentaccount.PaymentAccountType = paymentAccount.PaymentAccountType;
    paymentaccount.Description = paymentAccount.Description;
  }

  let message =
    'We are sorry - an internal error occurred when placing your order. Please try again later or call the restaurant to place your order.';

  if (error.response) {
    const result = error.response.data;

    if (result.UserMessage) {
      message = result.UserMessage;
    }
  }

  dispatch(
    SetOrder({
      state: 'failed',
      submitType,
      message,
    }),
  );
};

const MaybeAddVoucher = (
  voucherCode: string,
  orderId: number,
  paymentAccountId: number | null,
  basket: Basket.Data,
  options?: SubmitTypeOptions,
): Promise<any> => {
  // only apply if user overwritten auto-apply voucher
  const isUserInput =
    basket.voucher.status === 'valid' && basket.voucher.isUserInput;

  if (
    !isUserInput ||
    !voucherCode ||
    (options && options.paypal && !options.paypal.useSavedAccount) ||
    !paymentAccountId
  ) {
    return Promise.resolve(true);
  }
  return axios.post('/order/ApplyVoucherCodeToOrder', {
    orderId,
    voucherCode,
    paymentAccountId,
  }) as any;
};

const SubmitPaymentForOrder =
  (
    vrid: number,
    account: Api.PaymentAccount,
    orderId: number,
    voucherCode: string,
    chefNote: string,
    basket: Basket.Data,
    currency: Api.Currency,
    total: number,
    type?: OrderFlow.SubmitOrderType,
    options?: SubmitTypeOptions,
    concessionStoreId?: number,
  ) =>
  (
    dispatch: ThunkDispatch<Store.State, {}, Redux.Action>,
    getState: () => Store.State,
  ) => {
    dispatch(
      SetOrder({
        state: 'created',
        submitType: type,
        orderId,
        basket,
        chefNote,
        vrid,
        currency,
        total,
      }),
    );

    const accountId =
      account && account.PaymentAccountId ? account.PaymentAccountId : null;

    const addVoucher = MaybeAddVoucher(
      voucherCode,
      orderId,
      accountId,
      basket,
      options,
    );

    addVoucher.catch((e) =>
      OrderSubmissionFailure(
        dispatch,
        getState,
        'Add voucher to order failure',
        e,
        vrid,
        basket,
        type,
        account,
        orderId,
      ),
    );

    addVoucher.then(() => {
      if (!window.location.origin) {
        // For IE
        // @ts-ignore false positive on origin being read-only
        window.location.origin = `${window.location.protocol}//${
          window.location.port ? `:${window.location.port}` : ''
        }`;
      }

      const refererUrl = window.location.origin + window.location.pathname;

      let xhr;

      if (type === 'STRIPE' && options.googleApplePayStripeToken) {
        xhr = axios.post('/order/submitorderpaybutton', {
          orderId,
          stripePaymentMethodId: options.googleApplePayStripeToken,
          chefNote,
          refererUrl,
        });
      } else if (type === 'PAYPAL') {
        // Use PaymentAccountId from PayPal PyamentAccoun if it exists
        if (options.paypal.useSavedAccount) {
          xhr = axios.post('/order/submitorder', {
            orderId,
            paymentAccountId: account.PaymentAccountId,
            chefNote,
            refererUrl,
          });
        } else {
          xhr = axios.post('/order/submitorderpaypal', {
            orderId,
            clientToken: options.paypal.nonce,
            deviceData: options.paypal.deviceData,
            chefNote,
            refererUrl,
          });
        }
      } else {
        xhr = axios.post('/order/submitorder', {
          orderId,
          paymentAccountId: account.PaymentAccountId,
          chefNote,
          refererUrl,
        });
      }

      xhr.then((resp) => {
        if (resp.data.Data.RedirectUri) {
          // If redirect first track analytics
          analytics.eCommerce.purchase({
            // affiliation: `Flipdish - order - ${PhysicalRestaurantName}`,
            transaction_id: `${orderId}`,
            value: total,
            currency: Api.Currency[currency],
            items: basket.items.map((item) => ({
              id: `${item.Item.MenuItemId}`,
              name: item.Item.Name,
              price: item.Item.ActualPrice,
              quantity: 1, // TODO should group items
            })),
          });

          // And redirect
          window.location.href = resp.data.Data.RedirectUri;
        } else {
          const goto = concessionStoreId
            ? `/order/${orderId}/${concessionStoreId}`
            : `/order/${orderId}`;
          urlUtils.navigateToHash(goto);

          // use OrderResponse: resp.data.Data, ?
          dispatch(
            SetOrder({
              state: 'paid',
              submitType: type,
              orderId,
              basket,
              chefNote,
              vrid,
              currency,
              total,
            }),
          );

          const dispatchThunk = dispatch as any;

          dispatchThunk(SetCanCancel(15));
        }
      });

      xhr.catch((e) => {
        OrderSubmissionFailure(
          dispatch,
          getState,
          'Order submission failure',
          e,
          vrid,
          basket,
          type,
          account,
          orderId,
        );
      });
    });
  };

export const IncrementVouchersInProgress =
  (): Store.IncrementVouchersInProgress => ({
    type: 'INCREMENT_VOUCHERS_IN_PROGRESS',
  });

export const DecrementVouchersInProgress =
  (): Store.DecrementVouchersInProgress => ({
    type: 'DECREMENT_VOUCHERS_IN_PROGRESS',
  });

export const AddVoucherToOrder =
  (
    route: Routes,
    voucherCode: string,
    paymentAccountId: number,
    menuId: number,
    versionGuid: string,
  ) =>
  (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const state = getState();

    if (!route.params.type && !route.params.vrid) {
      return;
    }

    const deliveryType = route.params.type;
    const isDelivery = deliveryType === 'delivery';
    const vrId = Number(route.params.vrid);
    const prId = !isDelivery ? Number(route.params.id) : undefined;
    const deliveryLocationId = isDelivery ? Number(route.params.id) : undefined;

    const orderPayload = basketSelectors.getOrderPayload(state, {
      menuId,
      menuVersionGuid: versionGuid,
      deliveryType: route.params.type,
      vrId,
      prId,
      deliveryLocationId,
    });

    const setVoucher = (v: Vouchers.States) => {
      dispatch({
        type: 'SET_VOUCHER',
        payload: {
          voucher: v,
          menuId,
        },
      });
    };

    if (!orderPayload || typeof orderPayload === 'string') {
      console.warn(
        '[BASKET] AddVoucherToOrder ORDER_PAYLOAD_ERROR',
        orderPayload,
      );
      setVoucher({
        status: 'none',
        code: ' ',
      });
      dispatch({
        type: 'DECREMENT_VOUCHERS_IN_PROGRESS',
      });
      return;
    }

    setVoucher({
      status: 'loading',
      code: ' ',
      items: [],
    });

    const errorHandler = (axiosError: any) => {
      const message = Utilities.getUserMessage(axiosError);
      setVoucher({
        status: 'none',
        code: ' ',
      });
      dispatch({
        type: 'DECREMENT_VOUCHERS_IN_PROGRESS',
      });
      analytics.Track('Orders', 'Add Voucher Failure');
      const dispatchThunk = dispatch as any;
      dispatchThunk(AddToast(message));
    };

    // Dont send paymentAccountId here ever
    // Because it will try and auto-apply a voucher to the new order.
    // We want to be able to call ApplyVoucherCodeToOrder oursleves to choose the voucher applied
    const xhr = axios.post('/order/order', { ...orderPayload });
    xhr.catch(errorHandler);

    xhr.then((result) => {
      const orderId = result.data.Data.OrderId;

      const applyCode = Utilities.toPromise<Api.VoucherBasketItemDm[]>(
        axios.post('/order/ApplyVoucherCodeToOrder', {
          orderId,
          voucherCode,
          paymentAccountId,
        }),
      );
      applyCode.catch(errorHandler);
      applyCode.then((voucher) => {
        if (voucher[0]) {
          analytics.Track('Orders', 'Add Voucher Success', {
            event_label: voucher[0].Description,
            label: 'Add Voucher Success',
            section: 'Orders',
          });
        }

        dispatch({
          type: 'DECREMENT_VOUCHERS_IN_PROGRESS',
        });

        setVoucher({
          items: voucher,
          code: voucherCode,
          status: 'valid',
        });
      });
    });
  };

export const SubmitOrder =
  (
    order: Api.DeliveryOrder | Api.CollectionOrder,
    vrid: number,
    account: Api.PaymentAccount,
    voucherCode: string,
    chefNote: string,
    basket: Basket.Data,
    currency: Api.Currency,
    total: number,
    type?: OrderFlow.SubmitOrderType,
    options?: SubmitTypeOptions,
    concessionStoreId?: number,
  ) =>
  (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const dispatchThunk = dispatch as any;

    dispatch(
      SetOrder({
        state: 'creating',
        submitType: type,
      }),
    );

    let xhr: AxiosPromise<any>;
    if (basket.voucher.status === 'valid' && basket.voucher.isUserInput) {
      const noVoucherAutoApplyOrderPayload = {
        ...order,
        PaymentAccountId: undefined,
      };
      xhr = axios.post('/order/order', noVoucherAutoApplyOrderPayload);
    } else {
      xhr = axios.post('/order/order', order);
    }

    xhr.then((result) => {
      const orderId: number = result.data.Data.OrderId;

      dispatch(
        SetOrder({
          state: 'created',
          submitType: type,
          orderId,
          basket,
          chefNote,
          vrid,
          currency,
          total,
        }),
      );

      dispatchThunk(
        SubmitPaymentForOrder(
          vrid,
          account,
          orderId,
          voucherCode,
          chefNote,
          basket,
          currency,
          total,
          type,
          options,
          concessionStoreId,
        ),
      );
    });

    xhr.catch((e) =>
      OrderSubmissionFailure(
        dispatch,
        getState,
        'Order creation failed',
        e,
        vrid,
        basket,
        type,
      ),
    );
  };

export const setCollectionPreOrderTimes = (payload: {
  vrId: number;
  prId: number;
  preOrderTimes: Api.PreOrderTime[];
}) => ({
  type: 'SET_RESTAURANT_COLLECTION_PRE_ORDER_TIMES',
  payload,
});

export const setDeliveryPreOrderTimes = (payload: {
  vrId: number;
  dlId: number;
  preOrderTimes: Api.PreOrderTime[];
}) => ({
  type: 'SET_RESTAURANT_DELIVERY_PRE_ORDER_TIMES',
  payload,
});

export const ChangeLanguage =
  (lang: string) =>
  (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ): void => {
    const dispatchThunk = dispatch as any;

    const xhr = axios.put('/account/language/', {
      id: lang,
    });

    xhr.then(() => {
      Utilities.setLangauageInStorage(lang);
      // @ts-ignore flipdishLanguageSet type not recognised on window
      if (window.flipdishLanguageSet) {
        // @ts-ignore flipdishLanguageSet type not recognised on window
        window.flipdishLanguageSet(lang);
      } else {
        dispatchThunk(setupLanguage(lang));
      }

      // Once language changed reload restaurants as times come from the backend
      const deliveryLocations = getState().flipdish.DeliveryLocations;

      if (deliveryLocations.status === 'success') {
        deliveryLocations.data.forEach((dl) => {
          dispatchThunk(LoadDeliveryOptions(dl, false));
        });
      }

      const collectionSummaries = getState().flipdish.CollectionSummaries;
      if (collectionSummaries.status === 'success') {
        collectionSummaries.data.forEach((r: any) => {
          dispatchThunk(LoadCollectionDetails(r.PhysicalRestaurantId, true));
        });
      }
    });

    xhr.catch((e) => {
      console.log('Language change failed', e);
    });
  };

const loadDeliveryOptionsRequestTracker = new Set<string>();
export const LoadDeliveryOptions =
  (dl: Api.DeliveryLocation, _initial: boolean) =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const { Config, AppConfig } = getState().flipdish;

    const q = {
      Latitude: dl.Coordinates.Latitude,
      Longitude: dl.Coordinates.Longitude,
      skip: 0,
      count: 1000,
      deviceId: Config.DeviceId,
      deliveryLocationId: dl.DeliveryLocationId,
    };
    const trackerKey = JSON.stringify(q);

    // prevent 4+ extra unnecessary requests from who knows where
    if (loadDeliveryOptionsRequestTracker.has(trackerKey)) {
      return;
    }
    if (
      getState().deliveryOptions[q.deliveryLocationId] &&
      getState().deliveryOptions[q.deliveryLocationId].status === 'success'
    ) {
      return;
    }

    loadDeliveryOptionsRequestTracker.add(trackerKey);

    await axios
      .get('/Restaurant/DeliveryRestaurantSummaries', { params: q })
      .finally(() => {
        loadDeliveryOptionsRequestTracker.delete(trackerKey);
      })
      .then((response) => {
        const data = response.data.Data;

        if (AppConfig.status !== 'success') {
          // Raven.captureMessage('Cant load delivery options before app config', {
          //   tags: { category: 'Frontend error' },
          //   extra: {
          //     appnameid: Utilities.extraOption('data-restaurant') || '',
          //   },
          // });

          return;
        }

        /**
         * BUG WE R NOT CHECKING IF MENU IS LOADED... DOES IT NEEDS TO BE LOADED...
         */
        const skipTo = find(data, (r) => r.SkipToThisVirtualRestaurant);
        /* SUSPICIOUS
          DisplayDeliveryRestaurantListScreen | skipTo      | choiceOf  | result
          true                                | undefined   | [...]     | choice
          true                                | {...}       | [...]     | choice
          false                               | {...}       | [...]     | skip
          false                               | undefined   | [...]     | choice
          false                               | undefined   | [1]       | choice - but should it be skip?
        */
        if (!AppConfig.data.DisplayDeliveryRestaurantListScreen && skipTo) {
          dispatch(
            AddDeliveryOption(dl, {
              type: 'skip',
              to: skipTo,
            }),
          );
        } else {
          dispatch(
            AddDeliveryOption(dl, {
              type: 'choice',
              of: data,
            }),
          );
        }
      })
      .catch((_error) => {
        console.log(_error);
      });
  };

export const ClearDeliveryLocations = () => ({
  type: 'CLEAR_DELIVERY_LOCATIONS',
});

const SetCustomer = (payload: Data.Async<Api.Customer>): Store.SetCustomer => ({
  type: 'SET_CUSTOMER',
  payload,
});

const CustomerLoader = (): Promise<Api.Customer> => {
  let endpoint = '/account/accountdetails';
  const flipdishAppConfig =
    Store.getStoreInstance().getState().flipdish.AppConfig;

  if (flipdishAppConfig.status === 'success') {
    const appName = flipdishAppConfig.data.AppNameId;
    if (appName) {
      const lastPhysicalRestaurantId =
        Utilities.getLastPhysicalRestaurantId(appName);
      if (lastPhysicalRestaurantId) {
        endpoint += `?selectedPhysicalRestaurantId=${lastPhysicalRestaurantId}`;
      }
    }
  }

  return Utilities.toPromise<Api.Customer>(axios.get(endpoint));
};

export const getAccountDetailsUrl = (): string => {
  const appConfig = Store.getStoreInstance().getState().flipdish.AppConfig;

  const appNameId =
    appConfig.status === 'success' ? appConfig.data.AppNameId : undefined;
  const lastPhysicalRestaurantId =
    Utilities.getLastPhysicalRestaurantId(appNameId);

  let accountDetailsUrl = '/account/accountdetails';
  if (appConfig.status === 'success' && appNameId && lastPhysicalRestaurantId) {
    accountDetailsUrl += `?selectedPhysicalRestaurantId=${lastPhysicalRestaurantId}`;
  }

  return accountDetailsUrl;
};

export const SetCustomerName =
  (customerName: string, enqueueSnackbar: EnqueueSnackbar) =>
  (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    axios
      .put('/Account/CustomerName', { customerName })
      .then(() => {
        const translate = getTranslate(getState().locale);
        enqueueSnackbar(
          translate('Profile_snackbarMessage_nameUpdated') as string,
          { variant: 'success' },
        );
        // don't set the customer to 'pending' or reload all the data here.
        const loader = CustomerLoader();
        loader.then((customer) => {
          Utilities.setInLocalStorage('flipdish-customer', customer);

          dispatch(
            SetCustomer({
              status: 'success',
              data: customer,
            }),
          );
          Intercom.Update(getState().intercom);
        });
      })
      .catch((error) => {
        const message = Utilities.getUserMessage(error);
        enqueueSnackbar(message, { variant: 'error' });
      });
  };

export const SetCustomerEmail =
  (customerEmail: string, enqueueSnackbar: EnqueueSnackbar) =>
  (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    axios
      .post(
        '/Account/SetEmailAddress',
        { Email: customerEmail },
        { withCredentials: true },
      )
      .then(() => {
        const translate = getTranslate(getState().locale);
        enqueueSnackbar(
          translate('Profile_snackbarMessage_emailUpdated') as string,
          { variant: 'success' },
        );
        // don't set the customer to 'pending' or reload all the data here.
        const loader = CustomerLoader();
        loader.then((customer) => {
          Utilities.setInLocalStorage('flipdish-customer', customer);

          dispatch(
            SetCustomer({
              status: 'success',
              data: customer,
            }),
          );
          Intercom.Update(getState().intercom);
        });
      })
      .catch((error) => {
        const message = Utilities.getUserMessage(error);
        enqueueSnackbar(message, { variant: 'error' });
      });
  };

export const UpdateCustomer =
  () => async (dispatch: Redux.Dispatch<Redux.Action>) => {
    const accountDetailUrl = getAccountDetailsUrl();

    await axios
      .get(accountDetailUrl)
      .then((response) => {
        const data = response.data.Data;
        Utilities.setInLocalStorage('flipdish-customer', data);

        dispatch(
          SetCustomer({
            status: 'success',
            data,
          }),
        );
      })
      .catch((_error) => {
        console.log(_error);
      });
  };

const notLoggedInCustomerFlow = async (
  activeLanguage: string,
  deviceId: string,
  dispatch,
): Promise<void> => {
  const isAddi18nEnabled = flagService.isFlagOn('internationalisedAddress');
  if (isAddi18nEnabled) {
    const deliveryLocations = await getDeliveryLocations(
      deviceId,
      activeLanguage,
    );

    dispatch(loadDeliveryLocations(deliveryLocations));
  } else {
    dispatch(LoadDeliveryLocations());
  }

  dispatch(
    SetCustomer({
      status: 'none',
    }),
  );
};

export const LoadCustomer =
  (): ThunkResult<void> => async (dispatch, getState) => {
    const { flipdish, locale } = getState();
    const deviceId = flipdish.Config.DeviceId;

    const thunkDispatch = dispatch as any;

    const isAddi18nEnabled = flagService.isFlagOn('internationalisedAddress');

    if (getState().flipdish.Customer.status === 'success') {
      return;
    }

    const activeLanguage = activeLocaleInRedux(locale.languages);

    // Work with the cached version
    const customerJson = storage.getItem<Api.Customer>('flipdish-customer');

    if (customerJson) {
      dispatch(
        SetCustomer({
          status: 'success',
          data: customerJson,
        }),
      );

      Intercom.Update(getState().intercom);

      if (isAddi18nEnabled) {
        const deliveryLocations = await getDeliveryLocations(
          deviceId,
          activeLanguage,
        );

        thunkDispatch(loadDeliveryLocations(deliveryLocations));
      } else {
        thunkDispatch(LoadDeliveryLocations());
      }

      // Raven.setExtraContext({ PhoneNumber: customerJson.PhoneNumber });
      // Raven.setUserContext({
      //   email: customerJson.EmailAddress || undefined,
      //   username: customerJson.CustomerName || undefined,
      //   id: customerJson.UserId.toString(),
      // });
    }

    const flipdishToken = Utilities.getFlipdishTokenCookie();

    if (!flipdishToken) {
      await notLoggedInCustomerFlow(activeLanguage, deviceId, dispatch);
      return;
    }

    dispatch(SetCustomer({ status: 'pending' }));

    const loader = CustomerLoader();

    loader.then(async (customer) => {
      Utilities.setInLocalStorage('flipdish-customer', customer);

      dispatch(
        SetCustomer({
          status: 'success',
          data: customer,
        }),
      );
      dispatch(userActions.loginSuccessBridge() as any);

      Intercom.Update(getState().intercom);

      const customerLanguage = customer.Language;
      if (customerLanguage !== activeLanguage) {
        Utilities.setLangauageInStorage(customerLanguage);
        thunkDispatch(setupLanguage(customerLanguage));
      }

      if (isAddi18nEnabled) {
        const deliveryLocations = await getDeliveryLocations(
          deviceId,
          customerLanguage,
        );

        thunkDispatch(loadDeliveryLocations(deliveryLocations));
      } else {
        thunkDispatch(LoadDeliveryLocations());
      }
    });

    loader.catch(async () => {
      await notLoggedInCustomerFlow(activeLanguage, deviceId, dispatch);
      dispatch(userActions.loginErrorBridge() as any);
    });
  };

export const SetConsentPermission =
  (consent: boolean) =>
  (dispatch: Redux.Dispatch<any>, getState: () => Store.State) => {
    const param = consent ? 'true' : 'false';
    axios.put(`/Account/consent/${param}`).then(() => {
      // don't set the customer to 'pending' or reload all the data here.
      const loader = CustomerLoader();
      loader.then((customer) => {
        Utilities.setInLocalStorage('flipdish-customer', customer);

        dispatch(
          SetCustomer({
            status: 'success',
            data: customer,
          }),
        );

        Intercom.Update(getState().intercom);
      });
    });
  };

export const LoadPhoneNumberConfig =
  (coordinates: Api.Geo) => (dispatch: Redux.Dispatch<any>) => {
    const p = Utilities.toPromise<Api.PhoneNumberConfig>(
      axios.get('/Account/PhoneNumberConfig', {
        params: {
          Latitude: coordinates.Latitude,
          Longitude: coordinates.Longitude,
        },
      }),
    );

    p.then((config) => {
      const action: Store.LoadPhoneNumberConfig = {
        type: 'LOAD_PHONE_NUMBER_CONFIG',
        payload: config,
      };
      dispatch(action);
    });
  };

export const SetTheme = (themeName: WebOrderTheme): Store.SetTheme => ({
  type: 'SET_THEME',
  payload: themeName,
});

export const SetIsDarkTheme = (isDark: boolean): Store.SetIsDarkTheme => ({
  type: 'SET_IS_DARK_THEME',
  payload: isDark,
});

export const SetAppDesignScheme = (
  appDesignScheme: any,
): Store.SetAppDesignScheme => ({
  type: 'SET_APP_DESIGN_SCHEME',
  payload: appDesignScheme,
});

export default AddToast;
